import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, FileText } from "lucide-react";
import { api, apiBlob, saveBlob } from "@/lib/api-client";
import { currency, todayIso } from "@/lib/format";
import { DateInput } from "@/components/ui/DateInput";
import { Button } from "@/components/ui/Button";

// Invoices and credit notes issued in a date range - what goes into the GST return. Credit notes count negative.

type Row = { id: string; kind: "INVOICE" | "CREDIT_NOTE"; number: string; date: string; bookingRef: string; party: string; gstin: string; against: string; taxable: number; cgst: number; sgst: number; total: number };
type Totals = { taxable: number; cgst: number; sgst: number; total: number };

export function InvoiceRegister({ propertyId }: { propertyId: string }) {
  const today = todayIso();
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const valid = Boolean(from && to && from <= to);
  const range = `propertyId=${propertyId}&from=${from}&to=${to}`;
  const query = useQuery({
    queryKey: ["bookings", propertyId, "invoices", from, to],
    queryFn: () => api<{ rows: Row[]; totals: Totals }>(`/reports/invoices?${range}`),
    enabled: valid,
  });
  const download = useMutation({
    mutationFn: async () => saveBlob(await apiBlob(`/reports/invoices?${range}&format=csv`), `invoices-${from}-to-${to}.csv`),
  });
  const data = query.data;

  return (
    <section className="card reports-card collections-report" aria-labelledby="invoice-register-title">
      <div className="card__head">
        <h2 id="invoice-register-title">Invoices (GST)</h2>
        <Button variant="secondary" disabled={!valid || !data?.rows.length} loading={download.isPending} onClick={() => download.mutate()}><Download size={15} /> Excel (CSV)</Button>
      </div>
      <div className="collections-report__range">
        <label>From<DateInput max={to} value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>To<DateInput min={from} max={today} value={to} onChange={(event) => setTo(event.target.value)} /></label>
      </div>
      {!valid && <div className="form-error">Pick a valid date range.</div>}
      {query.isLoading && <div className="form-hint">Loading invoices...</div>}
      {(query.error || download.error) && <div className="form-error">{(query.error ?? download.error)!.message}</div>}
      {data && (
        !data.rows.length ? <div className="mini-empty"><FileText /><p>No invoices issued in this period.</p></div> : (
          <>
            <div className="kpi-strip collections-report__totals">
              <div><small>Taxable value</small><strong className="num">{currency.format(data.totals.taxable)}</strong><span className="kpi-strip__note">After credit notes</span></div>
              <div><small>CGST + SGST</small><strong className="num">{currency.format(data.totals.cgst + data.totals.sgst)}</strong><span className="kpi-strip__note">{currency.format(data.totals.cgst)} each side</span></div>
              <div><small>Invoiced total</small><strong className="num">{currency.format(data.totals.total)}</strong><span className="kpi-strip__note">{data.rows.length} document(s)</span></div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th scope="col">Date</th><th scope="col">Number</th><th scope="col">Party</th><th scope="col" className="num-col">Taxable</th><th scope="col" className="num-col">CGST</th><th scope="col" className="num-col">SGST</th><th scope="col" className="num-col">Total</th></tr></thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.date}</td>
                      <td>{row.number}{row.kind === "CREDIT_NOTE" && <small className="table-sub">Credit note · {row.against}</small>}</td>
                      <td>{row.party}{row.gstin && <small className="table-sub">GSTIN {row.gstin}</small>}</td>
                      <td className="num-col num">{currency.format(row.taxable)}</td>
                      <td className="num-col num">{currency.format(row.cgst)}</td>
                      <td className="num-col num">{currency.format(row.sgst)}</td>
                      <td className="num-col num">{currency.format(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </section>
  );
}
